import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { 
  IFundingApiResponse,
  IFundingFilterModel,
  IFundingSortModel,
  IFundingPaginationModel,
  IFundingListRequest,
  IReprocessFundingRequest,
  IReprocessFundingResponse,
  IOrganizationWithPropertiesResponse,
  FundingKeys
} from '@/types/admin/funding';

// Helper function to build query string (matching admin-web logic exactly)
const buildFundingListQuery = ({
  sortModel,
  filterModel,
  paginationModel,
  selectedOrganizationId,
}: {
  sortModel: IFundingSortModel[];
  filterModel: IFundingFilterModel;
  paginationModel: IFundingPaginationModel;
  selectedOrganizationId: number;
}): string => {
  // Field parameter mappings (exactly matching admin-web)
  const fieldParamMappings: { [key: string]: string } = {
    [FundingKeys.property]: "property",
    [FundingKeys.merchant_id]: "merchantId",
    [FundingKeys.funding_date]: "fundingDate",
    [FundingKeys.last_status]: "lastStatus",
  };

  const filteredFilterArgs = filterModel.items.filter((item) => {
    return item.value && item.value !== "";
  });

  const filterArgs = filteredFilterArgs.map((item) => {
    const fieldParam = fieldParamMappings[item.field] || item.field;
    
    if (item.operator.includes("not")) {
      return `${fieldParam}=!${item.value}`;
    }
    if (item.operator.includes("onOrAfter")) {
      return `${fieldParam}=>${item.value}`;
    }
    if (item.operator.includes("onOrBefore")) {
      return `${fieldParam}=<${item.value}`;
    }
    if (item.operator === "is" && Array.isArray(item.value)) {
      // Handle multi-select filters (property and merchantId)
      return item.value.map(val => `${fieldParam}=${val}`);
    }
    return `${fieldParam}=${item.value}`;
  }).flat();

  const combinedFilterArgs: { [key: string]: string[] } = {};
  filterArgs.forEach((arg: string) => {
    const splitArg = arg.split("=");
    if (combinedFilterArgs[splitArg[0]]) {
      combinedFilterArgs[splitArg[0]].push(splitArg[1]);
    } else {
      combinedFilterArgs[splitArg[0]] = [splitArg[1]];
    }
  });

  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;

  let paramString = `?organizationId=${selectedOrganizationId}&offset=${offset}&limit=${limit}`;
  
  Object.keys(combinedFilterArgs).forEach((key: string) => {
    paramString += `&${key}=${combinedFilterArgs[key].join(",")}`;
  });

  if (sortModel.length) {
    const sortField = fieldParamMappings[sortModel[0].field] || sortModel[0].field;
    const sortValue = sortModel[0].sort === "asc" ? sortField : `-${sortField}`;
    paramString += `&sortQuery=${sortValue}`;
  }

  return paramString;
};

// RTK Query API slice (matching admin-web pattern exactly)
export const fundingApi = createApi({
  reducerPath: 'fundingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['FundingList', 'FundingDetails'],
  endpoints: (builder) => ({
    // Get funding list with filtering, sorting, and pagination
    getFundingList: builder.query<IFundingApiResponse, {
      sortModel: IFundingSortModel[];
      filterModel: IFundingFilterModel;
      paginationModel: IFundingPaginationModel;
      selectedOrganizationId: number;
    }>({
      query: (params) => {
        const queryString = buildFundingListQuery(params);
        const fullUrl = `/api/v1/funding/list${queryString}`;
        return {
          url: fullUrl,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'FundingList', id: `organization-${arg.selectedOrganizationId}` },
        { type: 'FundingList', id: 'LIST' },
      ],
    }),

    // Reprocess fundings
    reprocessFundings: builder.mutation<IReprocessFundingResponse, IReprocessFundingRequest>({
      query: ({ ids, organizationId }) => ({
        url: '/api/v1/funding/reprocess',
        method: 'POST',
        body: { ids, organizationId },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'FundingList', id: `organization-${organizationId}` },
        { type: 'FundingList', id: 'LIST' },
      ],
    }),

    // Download fundings (for mobile we'll handle this differently)
    downloadFundings: builder.query<Blob, {
      ids: number[];
      organizationId: number;
    }>({
      query: ({ ids, organizationId }) => ({
        // Match admin-web style: ids=[1,2,3]
        url: `/api/v1/funding/download?organizationId=${organizationId}&ids=[${ids.join(",")}]`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Get organization details for property data (matching admin-web pattern)
    getOrganizationForFunding: builder.query<IOrganizationWithPropertiesResponse, {
      organizationId: number;
      includePropertyDetails?: boolean;
    }>({
      query: ({ organizationId, includePropertyDetails = true }) => ({
        url: `/api/v1/organizations/${organizationId}?includePropertyDetails=${includePropertyDetails}`,
        method: 'GET',
      }),
      providesTags: (result, error, { organizationId }) => [
        { type: 'FundingDetails', id: `organization-${organizationId}` },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetFundingListQuery,
  useLazyGetFundingListQuery,
  useReprocessFundingsMutation,
  useLazyDownloadFundingsQuery,
  useGetOrganizationForFundingQuery,
  useLazyGetOrganizationForFundingQuery,
} = fundingApi;
